import { Building2, Home, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { ZONES, ZONES_EXTRA, waQuote } from "../data/site";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Reveal, Kicker } from "../components/Reveal";

export default function ZonesPage() {
  const { lang, t } = useLang();
  const prefix = lang === "ca" ? "/ca" : "";

  return (
    <div data-testid="zones-page" className="pt-32">
      <Seo
        title={t("seo.zones.title")}
        desc={t("seo.zones.desc")}
        path="/zonas-de-servicio"
        jsonLd={breadcrumbLd([
          { name: "EcoGlow Detailing", path: prefix || "/" },
          { name: t("nav.zones"), path: `${prefix}/zonas-de-servicio` },
        ])}
      />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal><Kicker>{t("zones.kicker")}</Kicker></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("zones.title")}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">{t("zones.sub")}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-[#63E51D]/30 bg-[#101508] p-8">
              <span className="rounded-full bg-[#63E51D]/10 p-3 text-[#63E51D]"><Building2 className="h-6 w-6" /></span>
              <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">{t("zones.centerZone")} · Els Muntells</h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t("zones.centerDesc")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#121212] p-8">
              <span className="rounded-full bg-[#63E51D]/10 p-3 text-[#63E51D]"><Home className="h-6 w-6" /></span>
              <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">{t("zones.mobileZone")}</h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t("zones.mobileDesc")}</p>
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {ZONES.map((z, i) => (
            <Reveal key={z} delay={i * 0.05}>
              <div data-testid={`zones-page-zone-${i}`} className="rounded-full border border-white/10 bg-[#121212] px-4 py-3.5 text-center font-display text-lg font-bold uppercase tracking-wide text-zinc-200 transition-all duration-300 hover:border-[#63E51D]/60 hover:text-[#8BFF32]">
                {z}
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-zinc-500">
          {ZONES_EXTRA.join(" · ")} · {t("zones.note")}
        </p>
      </section>

      <section className="bg-[#080808] py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white">{t("zones.howTitle")}</h2>
          </Reveal>
          <div className="mt-10 space-y-5">
            {[t("zones.how1"), t("zones.how2"), t("zones.how3")].map((step, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-start gap-5 rounded-xl border border-white/5 bg-[#121212] p-6">
                  <span className="font-display text-3xl font-black text-[#63E51D]">{i + 1}</span>
                  <p className="text-sm leading-relaxed text-zinc-300">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <a
              href={waQuote(lang === "ca" ? "Hola EcoGlow! Vull consultar disponibilitat a la meva localitat." : "¡Hola EcoGlow! Quiero consultar disponibilidad en mi localidad.")}
              target="_blank" rel="noopener noreferrer"
              data-testid="zones-whatsapp-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#63E51D] px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(99,229,29,0.4)]"
            >
              <MessageCircle className="h-5 w-5" /> {t("nav.quote")}
            </a>
          </Reveal>
        </div>
      </section>
      <div className="pb-10" />
    </div>
  );
}
